import React from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { useUpgradePrompt } from '../../context/UpgradePromptContext';
import { TIER_LABEL } from '../../config/tiers';
import type { UpgradeGate } from '../../config/limits';

/**
 * Reusable lock for a gated FEATURE area. Shows a short line + an upgrade button
 * that opens the shared <UpgradePrompt>, so the same Stripe checkout flow is
 * reused everywhere. The button names the tier the gate actually needs, rather
 * than always saying Pro — sending someone to buy the wrong plan is worse than
 * not asking at all.
 *
 * Pass `children` to render a blurred, non-interactive preview behind the CTA
 * (the "free taste"); omit it for a plain locked card. Slate + bronze, theme-aware.
 */
interface Props {
  /** Short line shown on the lock panel. */
  message: string;
  /** The gate to open: its message, and which tier unlocks it. */
  gate: UpgradeGate;
  /** Optional preview blurred behind the overlay. */
  children?: React.ReactNode;
  /** Min height for the plain (no-preview) card. */
  minHeight?: number;
}

export function ProFeatureLock({ message, gate, children, minHeight = 150 }: Props) {
  const { open } = useUpgradePrompt();

  const cta = (
    <div className="flex flex-col items-center justify-center text-center gap-3 px-6 py-8">
      <span
        className="flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0"
        style={{ background: 'rgba(212,180,131,0.14)' }}
      >
        <Lock className="w-4 h-4" style={{ color: '#d4b483' }} />
      </span>
      <p className="text-[13px] max-w-md" style={{ color: 'var(--color-sub)' }}>
        {message}
      </p>
      <button
        type="button"
        onClick={() => open(gate)}
        className="flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-lg transition-transform hover:scale-[1.02]"
        style={{ backgroundColor: '#d4b483', color: '#1a1410' }}
      >
        <Sparkles className="w-3.5 h-3.5" />
        Upgrade to {TIER_LABEL[gate.tier ?? 'pro']}
      </button>
    </div>
  );

  if (!children) {
    return (
      <div
        className="rounded-xl flex items-center justify-center"
        style={{
          background: 'rgb(var(--overlay) / 0.02)',
          border: '0.5px solid rgb(var(--overlay) / 0.06)',
          minHeight,
        }}
      >
        {cta}
      </div>
    );
  }

  return (
    // minHeight floors the container so the absolute CTA overlay (lock + message +
    // button) always fits, even when the blurred preview is shorter than the CTA —
    // otherwise `overflow-hidden` clips the button off the bottom edge.
    <div className="relative rounded-xl overflow-hidden" style={{ border: '0.5px solid rgb(var(--overlay) / 0.06)', minHeight }}>
      <div aria-hidden className="pointer-events-none select-none" style={{ filter: 'blur(5px)', opacity: 0.55 }}>
        {children}
      </div>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ background: 'rgb(var(--overlay) / 0.03)' }}
      >
        {cta}
      </div>
    </div>
  );
}
